import {
  type DynamicModule,
  Global,
  Module,
  type Provider,
  type Type,
} from '@nestjs/common';
import { DiscoveryModule, ModuleRef } from '@nestjs/core';
import {
  AI_CACHE,
  AI_MODULE_OPTIONS,
  APPROVAL_GATE,
  CONVERSATION_STORE,
  RATE_LIMITER,
  RERANKER,
  VECTOR_STORE,
} from './ai.constants.js';
import type {
  AiFeatureOptions,
  AiModuleAsyncOptions,
  AiModuleOptions,
  ConversationStoreProvider,
} from './interfaces/ai-module-options.interface.js';
import { ProviderRegistry } from './core/provider-registry.js';
import { AiService } from './core/ai.service.js';
import { ToolRegistry } from './tools/tool.registry.js';
import { AgentExecutorService } from './agent/agent-executor.service.js';
import { EmbeddingsService } from './embeddings/embeddings.service.js';
import { InMemoryConversationStore } from './memory/in-memory-conversation.store.js';
import { ImageService } from './multimodal/image.service.js';
import { SpeechService } from './multimodal/speech.service.js';
import { TranscriptionService } from './multimodal/transcription.service.js';
import { RagService } from './rag/rag.service.js';
import { InMemoryVectorStore } from './rag/in-memory-vector-store.js';
import { PromptRegistry } from './prompts/prompt-registry.service.js';
import {
  AiEventEmitter,
  EVENT_EMITTER,
} from './observability/ai-event-emitter.js';
import { GuardrailRegistry } from './observability/guardrail.registry.js';
import { AgentRegistry } from './agent/orchestration/agent-registry.js';
import { McpService } from './mcp/mcp.service.js';
import { UsageTracker } from './usage/usage-tracker.service.js';
import { BudgetGuard } from './usage/budget.guardrail.js';
import { RateLimitGuardrail } from './ratelimit/rate-limit.guardrail.js';
import { SemanticMemory } from './memory/semantic/semantic-memory.service.js';
import { HeuristicReranker } from './rag/rerank/heuristic-reranker.js';
import { EvalRunner } from './evals/eval-runner.service.js';

import { BudgetPolicy } from './usage/budget-policy.service.js';
import { RunBudgetGuardrail } from './usage/run-budget.guardrail.js';
import {
  BUDGET_EXCEEDED_HANDLER,
  type BudgetExceededHandler,
} from './usage/on-budget-exceeded.interface.js';

const AI_INITIALIZER = Symbol('AI_INITIALIZER');

/**
 * Root module for `@mgvdev/nestjs-ai`. Registered globally so agents, tools and
 * services are available everywhere once configured.
 */
@Global()
@Module({})
export class AiModule {
  /** Configure the module with static options. */
  static forRoot(options: AiModuleOptions = {}): DynamicModule {
    return AiModule.build(
      { provide: AI_MODULE_OPTIONS, useValue: options },
      options,
    );
  }

  /** Configure the module asynchronously (e.g. from `ConfigService`). */
  static forRootAsync(options: AiModuleAsyncOptions): DynamicModule {
    return AiModule.build(
      {
        provide: AI_MODULE_OPTIONS,
        useFactory: options.useFactory,
        inject: options.inject ?? [],
      },
      options,
      options.imports,
    );
  }

  /**
   * Convenience registration of agents / tools / guardrails so they don't have
   * to be added to a consuming module's own `providers` array, plus feature
   * prompts. Discovery still finds providers globally regardless of where they
   * are registered.
   */
  static forFeature(feature: AiFeatureOptions = {}): DynamicModule {
    const providers: Provider[] = [
      ...(feature.agents ?? []),
      ...(feature.tools ?? []),
      ...(feature.guardrails ?? []),
    ];
    if (feature.prompts?.length) {
      providers.push({
        provide: Symbol('AI_FEATURE_PROMPTS'),
        useFactory: (registry: PromptRegistry) => {
          registry.registerAll(feature.prompts!);
          return true;
        },
        inject: [PromptRegistry],
      });
    }
    return { module: AiModule, providers, exports: providers };
  }

  private static build(
    optionsProvider: Provider,
    options: Partial<AiModuleOptions> & {
      conversationStore?: ConversationStoreProvider;
    },
    extraImports: DynamicModule['imports'] = [],
  ): DynamicModule {
    return {
      module: AiModule,
      imports: [DiscoveryModule, ...(extraImports ?? [])],
      providers: [
        optionsProvider,
        AiModule.storeProvider(options.conversationStore),
        AiModule.vectorStoreProvider(options.vectorStore),
        AiModule.eventEmitterProvider(),
        ...AiModule.tokenProvider(AI_CACHE, options.cache),
        ...AiModule.tokenProvider(APPROVAL_GATE, options.approvalGate),
        ...AiModule.tokenProvider(RATE_LIMITER, options.rateLimiter),
        ...AiModule.guardrailClasses(options.guardrails),
        ...(options.maxCostPerConversation != null ? [BudgetGuard] : []),
        ...(options.rateLimiter ? [RateLimitGuardrail] : []),
        ...(options.budget != null ? [RunBudgetGuardrail] : []),
        ...AiModule.budgetExceededHandlerProvider(options.budgetExceededHandler),
        ...AiModule.coreProviders(),
        AiModule.initializerProvider(),
      ],
      exports: AiModule.exportedTokens(),
    };
  }

  private static coreProviders(): Provider[] {
    return [
      ProviderRegistry,
      ToolRegistry,
      AgentExecutorService,
      AgentRegistry,
      AiService,
      EmbeddingsService,
      ImageService,
      SpeechService,
      TranscriptionService,
      RagService,
      PromptRegistry,
      AiEventEmitter,
      GuardrailRegistry,
      McpService,
      UsageTracker,
      BudgetPolicy,
      SemanticMemory,
      EvalRunner,
      { provide: RERANKER, useFactory: () => new HeuristicReranker() },
    ];
  }

  /**
   * Registers an optional provider (class / factory / value / useClass shape)
   * under `token`, or nothing when not configured.
   */
  private static tokenProvider(
    token: symbol,
    config:
      | Type<any>
      | { useClass?: Type<any>; useFactory?: (...a: any[]) => any; useValue?: any; inject?: any[] }
      | undefined,
  ): Provider[] {
    if (!config) {
      return [];
    }
    if (typeof config === 'function') {
      return [{ provide: token, useClass: config }];
    }
    return [{ provide: token, ...config } as Provider];
  }

  private static exportedTokens(): any[] {
    return [
      ProviderRegistry,
      ToolRegistry,
      AgentExecutorService,
      AgentRegistry,
      AiService,
      EmbeddingsService,
      ImageService,
      SpeechService,
      TranscriptionService,
      RagService,
      PromptRegistry,
      AiEventEmitter,
      GuardrailRegistry,
      McpService,
      UsageTracker,
      SemanticMemory,
      EvalRunner,
      RERANKER,
      CONVERSATION_STORE,
      VECTOR_STORE,
      AI_MODULE_OPTIONS,
    ];
  }

  private static guardrailClasses(guardrails?: Type<any>[]): Provider[] {
    return guardrails ?? [];
  }

  private static budgetExceededHandlerProvider(
    handler: AiModuleOptions['budgetExceededHandler'],
  ): Provider[] {
    if (!handler) {
      return [];
    }
    if (typeof handler === 'function') {
      return [{ provide: BUDGET_EXCEEDED_HANDLER, useClass: handler }];
    }
    return [{ provide: BUDGET_EXCEEDED_HANDLER, ...handler } as Provider];
  }

  /** Seeds prompts (and future startup wiring) once dependencies exist. */
  private static initializerProvider(): Provider {
    return {
      provide: AI_INITIALIZER,
      useFactory: (registry: PromptRegistry, opts: AiModuleOptions) => {
        if (opts.prompts?.length) {
          registry.registerAll(opts.prompts);
        }
        return true;
      },
      inject: [PromptRegistry, AI_MODULE_OPTIONS],
    };
  }

  /**
   * Optionally resolves `EventEmitter2` from `@nestjs/event-emitter` if the
   * package is installed and its module is imported; otherwise `undefined`.
   */
  private static eventEmitterProvider(): Provider {
    return {
      provide: EVENT_EMITTER,
      useFactory: async (moduleRef: ModuleRef) => {
        try {
          const mod: any = await import('@nestjs/event-emitter');
          return moduleRef.get(mod.EventEmitter2, { strict: false });
        } catch {
          return undefined;
        }
      },
      inject: [ModuleRef],
    };
  }

  private static storeProvider(store?: ConversationStoreProvider): Provider {
    if (!store) {
      return {
        provide: CONVERSATION_STORE,
        useClass: InMemoryConversationStore,
      };
    }
    if (typeof store === 'function') {
      return { provide: CONVERSATION_STORE, useClass: store };
    }
    return { provide: CONVERSATION_STORE, ...store } as Provider;
  }

  private static vectorStoreProvider(
    store?: AiModuleOptions['vectorStore'],
  ): Provider {
    if (!store) {
      return { provide: VECTOR_STORE, useClass: InMemoryVectorStore };
    }
    if (typeof store === 'function') {
      return { provide: VECTOR_STORE, useClass: store };
    }
    return { provide: VECTOR_STORE, ...store } as Provider;
  }
}
